// src/pages/DashboardAdmin.jsx
import React, { useEffect, useMemo, useState } from "react";
import { Box, Toolbar, Tabs, Tab, Paper, TextField, Button, Table, TableHead, TableRow, TableCell, TableBody, Typography, Grid } from "@mui/material";
import Sidebar from "../components/Sidebar";
import Navbar from "../components/Navbar";
import { empleados, percepciones, deducciones } from "../services/api";

function useQueryTab() {
  const params = new URLSearchParams(window.location.search);
  const t = params.get("tab");
  return t || "empleados";
}

export default function DashboardAdmin() {
  const initialTab = useQueryTab();
  const [tab, setTab] = useState(initialTab);
  const [listEmpleados, setListEmpleados] = useState([]);
  const [listPer, setListPer] = useState([]);
  const [listDed, setListDed] = useState([]);
  const [loading, setLoading] = useState(false);

  const refresh = async () => {
    setLoading(true);
    try {
      const [e, p, d] = await Promise.all([empleados.list(), percepciones.list(), deducciones.list()]);
      setListEmpleados(e.data || []);
      setListPer(p.data || []);
      setListDed(d.data || []);
    } catch (e) {
      alert("Error cargando datos");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); }, []);

  return (
    <Box sx={{ display: "flex" }}>
      <Navbar />
      <Sidebar />
      <Box component="main" sx={{ flexGrow: 1, p: 3 }}>
        <Toolbar />
        <Typography variant="h5" mb={2}>Dashboard Administrativo</Typography>
        <Paper sx={{ mb: 2 }}>
          <Tabs value={tab} onChange={(e, v) => setTab(v)}>
            <Tab label="Empleados" value="empleados" />
            <Tab label="Percepciones" value="percepciones" />
            <Tab label="Deducciones" value="deducciones" />
          </Tabs>
        </Paper>

        {tab === "empleados" && <EmpleadosTab data={listEmpleados} onCreated={refresh} loading={loading} />}
        {tab === "percepciones" && <PercepcionesTab data={listPer} empleados={listEmpleados} onCreated={refresh} loading={loading} />}
        {tab === "deducciones" && <DeduccionesTab data={listDed} empleados={listEmpleados} onCreated={refresh} loading={loading} />}
      </Box>
    </Box>
  );
}

function EmpleadosTab({ data, onCreated, loading }) {
  const [form, setForm] = useState({
    nombre: "", rol: "", sucursal: "", fecha_ingreso: "", sueldo_base: "", correo: "", password: ""
  });
  const handle = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    try {
      await empleados.create(form);
      setForm({ nombre: "", rol: "", sucursal: "", fecha_ingreso: "", sueldo_base: "", correo: "", password: "" });
      onCreated?.();
      alert("Empleado registrado");
    } catch {
      alert("Error al registrar");
    }
  };

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} md={5}>
        <Paper sx={{ p: 2 }}>
          <Typography variant="subtitle1" mb={1}>Registrar empleado</Typography>
          <form onSubmit={submit}>
            {[
              ["nombre", "Nombre"],
              ["rol", "Rol (Administrador, Gerente, ... )"],
              ["sucursal", "Sucursal"],
              ["fecha_ingreso", "Fecha de ingreso (YYYY-MM-DD)"],
              ["sueldo_base", "Sueldo base"],
              ["correo", "Correo"],
              ["password", "Contraseña (temporal desarrollo)"]
            ].map(([name, label]) => (
              <TextField
                key={name}
                name={name}
                label={label}
                type={name === "password" ? "password" : name === "sueldo_base" ? "number" : "text"}
                fullWidth
                margin="dense"
                value={form[name]}
                onChange={handle}
              />
            ))}
            <Button type="submit" variant="contained" disabled={loading} sx={{ mt: 1 }}>Guardar</Button>
          </form>
        </Paper>
      </Grid>
      <Grid item xs={12} md={7}>
        <Paper sx={{ p: 2 }}>
          <Typography variant="subtitle1" mb={1}>Empleados ({data.length})</Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Nombre</TableCell>
                <TableCell>Rol</TableCell>
                <TableCell>Sucursal</TableCell>
                <TableCell>Ingreso</TableCell>
                <TableCell>Sueldo</TableCell>
                <TableCell>Correo</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.map((r) => (
                <TableRow key={r.id}>
                  <TableCell>{r.nombre}</TableCell>
                  <TableCell>{r.rol}</TableCell>
                  <TableCell>{r.sucursal}</TableCell>
                  <TableCell>{r.fecha_ingreso}</TableCell>
                  <TableCell>${Number(r.sueldo_base || 0).toFixed(2)}</TableCell>
                  <TableCell>{r.correo}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      </Grid>
    </Grid>
  );
}

function PercepcionesTab({ data, empleados: emps, onCreated, loading }) {
  const [form, setForm] = useState({
    empleado_id: "", tipo: "", monto: "", descripcion: ""
  });
  const handle = (e) => setForm({ ...form, [e.target.name]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    try {
      await percepciones.create({ ...form, monto: Number(form.monto || 0) });
      setForm({ empleado_id: "", tipo: "", monto: "", descripcion: "" });
      onCreated?.();
      alert("Percepción registrada");
    } catch {
      alert("Error al registrar percepción");
    }
  };

  const tipos = useMemo(() => [
    "Aguinaldo","Vacaciones","Prima vacacional","Tiempo extra","Sueldo","Incapacidad"
  ], []);

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} md={5}>
        <Paper sx={{ p: 2 }}>
          <Typography variant="subtitle1" mb={1}>Registrar percepción</Typography>
          <form onSubmit={submit}>
            <TextField
              select SelectProps={{ native: true }}
              name="empleado_id" label="Empleado" fullWidth margin="dense"
              value={form.empleado_id} onChange={handle}
            >
              <option value="">Selecciona...</option>
              {emps.map((e) => <option key={e.id} value={e.id}>{e.nombre}</option>)}
            </TextField>
            <TextField
              select SelectProps={{ native: true }}
              name="tipo" label="Tipo" fullWidth margin="dense"
              value={form.tipo} onChange={handle}
            >
              <option value="">Selecciona...</option>
              {tipos.map((t) => <option key={t} value={t}>{t}</option>)}
            </TextField>
            <TextField name="monto" label="Monto" type="number" fullWidth margin="dense" value={form.monto} onChange={handle} />
            <TextField name="descripcion" label="Descripción" fullWidth margin="dense" value={form.descripcion} onChange={handle} />
            <Button type="submit" variant="contained" disabled={loading} sx={{ mt: 1 }}>Guardar</Button>
          </form>
        </Paper>
      </Grid>
      <Grid item xs={12} md={7}>
        <Paper sx={{ p: 2 }}>
          <Typography variant="subtitle1" mb={1}>Percepciones ({data.length})</Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Empleado</TableCell>
                <TableCell>Tipo</TableCell>
                <TableCell>Monto</TableCell>
                <TableCell>Fecha</TableCell>
                <TableCell>Descripción</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.map((r) => (
                <TableRow key={r.id}>
                  <TableCell>{r.empleado_id}</TableCell>
                  <TableCell>{r.tipo}</TableCell>
                  <TableCell>${Number(r.monto || 0).toFixed(2)}</TableCell>
                  <TableCell>{r.fecha}</TableCell>
                  <TableCell>{r.descripcion}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      </Grid>
    </Grid>
  );
}

function DeduccionesTab({ data, empleados: emps, onCreated, loading }) {
  const [form, setForm] = useState({
    empleado_id: "", tipo: "", monto: "", descripcion: ""
  });
  const handle = (e) => setForm({ ...form, [e.target.name]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    try {
      await deducciones.create({ ...form, monto: Number(form.monto || 0) });
      setForm({ empleado_id: "", tipo: "", monto: "", descripcion: "" });
      onCreated?.();
      alert("Deducción registrada");
    } catch {
      alert("Error al registrar deducción");
    }
  };
  const tipos = ["Consumo","Uniforme","Anticipo","Otro"];

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} md={5}>
        <Paper sx={{ p: 2 }}>
          <Typography variant="subtitle1" mb={1}>Registrar deducción</Typography>
          <form onSubmit={submit}>
            <TextField
              select SelectProps={{ native: true }}
              name="empleado_id" label="Empleado" fullWidth margin="dense"
              value={form.empleado_id} onChange={handle}
            >
              <option value="">Selecciona...</option>
              {emps.map((e) => <option key={e.id} value={e.id}>{e.nombre}</option>)}
            </TextField>
            <TextField
              select SelectProps={{ native: true }}
              name="tipo" label="Tipo" fullWidth margin="dense"
              value={form.tipo} onChange={handle}
            >
              <option value="">Selecciona...</option>
              {tipos.map((t) => <option key={t} value={t}>{t}</option>)}
            </TextField>
            <TextField name="monto" label="Monto" type="number" fullWidth margin="dense" value={form.monto} onChange={handle} />
            <TextField name="descripcion" label="Descripción" fullWidth margin="dense" value={form.descripcion} onChange={handle} />
            <Button type="submit" variant="contained" disabled={loading} sx={{ mt: 1 }}>Guardar</Button>
          </form>
        </Paper>
      </Grid>
      <Grid item xs={12} md={7}>
        <Paper sx={{ p: 2 }}>
          <Typography variant="subtitle1" mb={1}>Deducciones ({data.length})</Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Empleado</TableCell>
                <TableCell>Tipo</TableCell>
                <TableCell>Monto</TableCell>
                <TableCell>Fecha</TableCell>
                <TableCell>Descripción</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.map((r) => (
                <TableRow key={r.id}>
                  <TableCell>{r.empleado_id}</TableCell>
                  <TableCell>{r.tipo}</TableCell>
                  <TableCell>${Number(r.monto || 0).toFixed(2)}</TableCell>
                  <TableCell>{r.fecha}</TableCell>
                  <TableCell>{r.descripcion}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      </Grid>
    </Grid>
  );
}
